/* variant-footer-link.js
 * ---------------------------------------------------------------------------
 * Footer link shown ONLY while a specific product variant is selected on its
 * PDP. DUMB BY DESIGN: this file ships NO variant ids, labels, or URLs. On each
 * variant selection it asks the backend `/api/variant-link?variant=<id>`, which
 * returns ONLY that one variant's link (or null). So reading this file (or the
 * page source) reveals nothing about which variant is special or where it links.
 * The map is managed in the control panel → "Variant footer links" section, and
 * the backend never exposes the full map publicly (admin-only) — only the single
 * requested variant. Reacts live to the theme's 'variant:change' event + the
 * initial selected variant.
 *
 * Ported from the B2C-ROW-US pilot (agilite-pilot-port). The ONLY change vs the
 * pilot copy: the fetch sends an X-Store-Region header from window.AGB_STORE_REGION
 * (set per store in theme.liquid) so EU/IL PREVIEW themes — which run on a
 * myshopify/shopifypreview domain the backend can't region-infer from Origin —
 * read their OWN variant map instead of falling back to ROW's. Same mechanism
 * custom-labels.js uses.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var SLOT_ID = 'variant-footer-link';

  // Panel-backend base, derived from window.SE_CONFIG_URL (same source the
  // scarcity engine / custom-labels use), e.g. ".../api/engine-config" -> base.
  function baseUrl() {
    try {
      var raw = (typeof window !== 'undefined' && window.SE_CONFIG_URL) || '';
      if (!raw) return null;
      var m = raw.match(/^(.*)\/api\/[^/]+$/);
      return (m && m[1]) ? m[1] : raw.replace(/\/+$/, '');
    } catch (e) { return null; }
  }

  // Live theme -> live config; preview/unpublished/dev -> preview config.
  function envParam() {
    try {
      var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
      return (role && role !== 'main') ? 'preview' : 'live';
    } catch (e) { return 'live'; }
  }

  function urlVariantId() {
    try { return new URL(window.location.href).searchParams.get('variant'); } catch (e) { return null; }
  }
  function pickerVariantId() {
    try {
      var input = document.querySelector(
        'product-info input[name="id"], form[action*="/cart/add"] input[name="id"], input[name="id"]'
      );
      return input && input.value ? input.value : null;
    } catch (e) { return null; }
  }
  function currentVariantId() { return urlVariantId() || pickerVariantId(); }

  // Per-variant cache (value: {label,href} | null) so re-selecting a variant
  // doesn't refetch. `latest` is the race guard for rapid swatch switching.
  var cache = Object.create(null);
  var inflight = Object.create(null);
  var latest = null;

  function fetchLink(variantId, cb) {
    if (variantId in cache) { cb(cache[variantId]); return; }
    var base = baseUrl();
    if (!base) { cb(null); return; }
    if (inflight[variantId]) return;
    inflight[variantId] = true;
    var env = envParam();
    var url = base + '/api/variant-link?variant=' + encodeURIComponent(variantId) +
              (env === 'preview' ? '&env=preview' : '');
    // Declare this store's region explicitly (same as custom-labels.js): a PREVIEW
    // theme runs on a myshopify/shopifypreview domain, so the backend's Origin/Referer
    // inference would misclassify it as US-ROW. window.AGB_STORE_REGION (set in
    // theme.liquid: 'International-EU' | 'IL' | 'US-ROW') forces the right map.
    var _hdrs = { Accept: 'application/json' };
    try { var _rgn = (typeof window !== 'undefined' && window.AGB_STORE_REGION) || ''; if (_rgn) _hdrs['X-Store-Region'] = _rgn; } catch (e) {}
    fetch(url, { headers: _hdrs })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var link = (j && j.link) ? j.link : null;
        cache[variantId] = link;
        delete inflight[variantId];
        cb(link);
      })
      .catch(function () { delete inflight[variantId]; cb(null); });
  }

  function footerHost() {
    return document.querySelector('.footer__inner')
        || document.querySelector('.footer .container')
        || document.querySelector('.footer')
        || document.querySelector('footer');
  }
  function clearLink() {
    var el = document.getElementById(SLOT_ID);
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }
  function showLink(link) {
    var host = footerHost();
    if (!host) return;
    var el = document.getElementById(SLOT_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = SLOT_ID;
      el.className = 'variant-footer-link';
      el.style.width = '100%';
      el.style.gridColumn = '1 / -1';   // span ALL footer grid columns
      el.style.margin = '16px 0 0';
      el.style.textAlign = 'center';
      var a = document.createElement('a');
      a.setAttribute('data-variant-footer-link', '1');
      // Plain footer-style link: inherit the footer's colour + font, NO box and
      // NO underline so it matches the other footer text. Position is unchanged
      // (set on the wrapper); the pointer cursor keeps it feeling clickable.
      a.style.color = 'inherit';
      a.style.font = 'inherit';
      a.style.textDecoration = 'none';
      a.style.cursor = 'pointer';
      el.appendChild(a);
      // Top of the footer content (not the bottom — that sits under the preview bar).
      host.insertBefore(el, host.firstChild);
    }
    var lk = el.querySelector('a');
    lk.textContent = link.label || 'Learn more';
    // Backend returns a ready-to-use URL (absolute or site-relative). It's a
    // shopper-visible href, so guard the scheme (never javascript:/data:).
    lk.href = safeHref(link.url);
  }

  // Allow http(s) / mailto / tel / site-relative only; anything else -> '#'.
  function safeHref(u) {
    var s = (u == null ? '' : String(u)).trim();
    if (!s) return '#';
    var low = s.toLowerCase();
    if (low.indexOf('javascript:') === 0 || low.indexOf('data:') === 0 || low.indexOf('vbscript:') === 0) return '#';
    if (low.indexOf('http://') === 0 || low.indexOf('https://') === 0 ||
        low.indexOf('mailto:') === 0 || low.indexOf('tel:') === 0 || s.charAt(0) === '/') return s;
    return '#';
  }

  function update(variantId) {
    latest = variantId;
    if (!variantId) { clearLink(); return; }
    fetchLink(variantId, function (link) {
      if (variantId !== latest) return;          // a newer variant was selected mid-fetch
      if (link && link.url) showLink(link); else clearLink();
    });
  }

  window.addEventListener('variant:change', function (e) {
    var d = e && e.detail;
    var id = (d && d.variant && d.variant.id) || (d && d.variantId) || urlVariantId();
    update(id != null ? String(id) : null);
  });

  function init() { update(currentVariantId()); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
